/**
 * WaveTrace Tracker - Comprehensive user tracking with enhanced identification
 *
 * @example
 * ```typescript
 * import { WaveTracker } from 'wavetrace-tracker';
 *
 * const tracker = new WaveTracker({
 *   endpoint: 'https://your-api.com/track',
 *   autoTrackPageViews: true,
 *   autoTrackClicks: true,
 *   enableEngagement: true,         // Track time, scroll, interactions
 *   enableJourney: true,            // Track user journey
 *   includeDeviceContext: true,     // Include detailed device info
 * });
 *
 * // Identify user
 * tracker.identify({
 *   userId: '12345',
 *   username: 'john_doe',
 *   wallet: '0x1234...',
 * });
 *
 * // Track custom event
 * tracker.track('button_click', {
 *   buttonName: 'sign_up',
 *   campaign: 'summer_2024',
 * });
 *
 * // Define and track funnels
 * tracker.defineFunnel({
 *   name: 'signup',
 *   steps: [
 *     { name: 'visit_landing' },
 *     { name: 'click_signup' },
 *     { name: 'complete_form' },
 *     { name: 'verify_email' }
 *   ]
 * });
 *
 * tracker.trackFunnelStep('signup', 'visit_landing');
 * ```
 */

// Main tracker
export { WaveTracker } from './tracker';

// Types
export type {
  TrackerConfig,
  RetryConfig,
  ClickTrackingMode,
  ClickTrackingConfig,
  UserIdentity,
  TrackingEvent,
  PageViewEvent,
  ClickEvent,
  CustomEvent,
  WalletConnectedEvent,
  WalletDisconnectedEvent,
  Event,
} from './types';

// Journey and funnel types
export type {
  FunnelDefinition,
  FunnelStep,
  FunnelProgress,
  JourneyEvent,
  Journey,
} from './utils/journey';

// Utilities (for advanced usage)
export {
  getAnonymousId,
  getIdentityInfo,
} from './utils/identity';

export {
  JourneyTracker,
  FunnelTracker,
  AttributionTracker,
} from './utils/journey';

export {
  EngagementTracker,
  TimeTracker,
  ScrollTracker,
} from './utils/engagement';

export {
  getDeviceContext,
  getBrowserInfo,
  getOSInfo,
  getDeviceType,
} from './utils/browser';

export {
  WalletDetector,
  isWalletAvailable,
  getWalletProvider,
} from './utils/wallet-detection';

export type {
  WalletProvider,
  WalletInfo,
  WalletEventHandler,
  WalletDisconnectHandler,
} from './utils/wallet-detection';

// Default export for convenience
export { WaveTracker as default } from './tracker';
